<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'
import { market } from '@/modules/shop/mixins/market'

import store from '@/store'

import { overWriteAxiosHeaders } from "@/helpers/authHelper"

const LayoutVertical = () => import('@/modules/layouts/layout-vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/modules/layouts/layout-horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/modules/layouts/layout-full/LayoutFull.vue')

export default {
  mixins: [market],
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    ...mapState('auth',['isLogged','user']),
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    isIdle() {
      return this.$store.state.idleVue.isIdle
    }
  },
  //si idle-vue detecta falta de inactividad se hace logout
  watch: {
    isIdle (idleValor) {
      if( idleValor && this.isLogged){
        this._logout()
      }
    },
    user(){
      this.setUserToken()
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  created(){
    this.clearStorage()
    this.setUserToken()
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('appConfig/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('appConfig/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  data() {
    return {
      timeOver: this.$store.state.idleVue.isIdle
    }
  },
  methods:{
    ...mapMutations('auth',['logout']),
    ...mapMutations('shop',['unsetCanalVenta','unsetPuntoDeVenta','setEmptyCart','unsetLastNameSearch','unsetDataRoom', 'setGlobalCurrency', 'setCurrency']),
    ...mapMutations('products',['setProducts','setSelectedCategory']),
    ...mapMutations('bookings',['resetToInitialState']),

    _logout(){
      this.logout()
      this.unsetCanalVenta()
      this.unsetPuntoDeVenta()
      this.setEmptyCart()
      this.unsetLastNameSearch()
      this.unsetDataRoom()
      this.resetToInitialState()
      this.setProducts([])
      this.setSelectedCategory(null)
      this.setGlobalCurrency(null)
      this.setCurrency(null)
      localStorage.removeItem('vuex')
      window.location.reload(true)
    },
    setUserToken(){
      if (this.isLogged && this.user?.token) overWriteAxiosHeaders(this.user?.token)
    }
  }
}
</script>
